import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import Layout from '@/components/Layout';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Search, Building, Layers, FileText, Download } from 'lucide-react';
import { toast } from 'sonner';

const STATUS_COLORS = {
  not_started: 'bg-blue-500',
  in_progress: 'bg-amber-500',
  complete: 'bg-emerald-500',
  blocked: 'bg-red-500',
};

const STATUS_LABELS = {
  not_started: 'Not Started',
  in_progress: 'In Progress',
  complete: 'Complete',
  blocked: 'Blocked',
};

function StatusDot({ status, label }) {
  const isBlocked = status === 'blocked';
  return (
    <div className="flex flex-col items-center gap-0.5" title={`${label}: ${STATUS_LABELS[status] || 'Unknown'}`}>
      <div className={`w-2.5 h-2.5 rounded-full ${STATUS_COLORS[status] || 'bg-slate-300'} ${isBlocked ? 'status-dot-blocked' : ''}`} />
      <span className="text-[9px] text-slate-400 font-medium leading-none">{label}</span>
    </div>
  );
}

function UnitCard({ unit, onClick }) {
  const stages = unit.stages || {};
  const displayName = unit.type === 'communal' && !/communal/i.test(unit.name)
    ? `${unit.name} Communal`
    : unit.name;
  return (
    <div
      onClick={onClick}
      className="flex flex-col p-4 bg-white border border-slate-200 rounded-xl hover:border-blue-400 cursor-pointer transition-colors space-y-3"
      data-testid={`unit-card-${unit.name.replace(/\s+/g, '-').toLowerCase()}`}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {unit.type === 'communal' ? <Layers className="h-4 w-4 text-emerald-600" /> : <Building className="h-4 w-4 text-blue-600" />}
          <span className="font-semibold text-sm text-slate-900">{displayName}</span>
        </div>
        <Badge variant="outline" className="text-[10px] capitalize">{unit.type}</Badge>
      </div>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <span className="text-[9px] text-slate-500 font-semibold tracking-wider uppercase">Progress</span>
            <StatusDot status={stages.progress_1} label="1" />
            <StatusDot status={stages.progress_2} label="2" />
            <StatusDot status={stages.progress_3} label="3" />
          </div>
          <div className="w-px h-4 bg-slate-200" />
          <div className="flex items-center gap-2">
            <span className="text-[9px] text-slate-500 font-semibold tracking-wider uppercase">QA</span>
            <StatusDot status={stages.qa_1} label="1" />
            <StatusDot status={stages.qa_2} label="2" />
            <StatusDot status={stages.qa_3} label="3" />
          </div>
        </div>
      </div>
    </div>
  );
}

function useUnitsData() {
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [areaType, setAreaType] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');

  const fetchUnits = useCallback(async () => {
    try {
      const params = {};
      if (search) params.search = search;
      if (areaType !== 'all') params.area_type = areaType;
      if (statusFilter !== 'all') params.status_filter = statusFilter;
      const resp = await api.get('/units', { params });
      setUnits(resp.data);
    } catch {
      toast.error('Failed to load units');
    } finally {
      setLoading(false);
    }
  }, [search, areaType, statusFilter]);

  useEffect(() => { fetchUnits(); }, [fetchUnits]);

  const flats = useMemo(() => units.filter(u => u.type === 'flat'), [units]);
  const communal = useMemo(() => units.filter(u => u.type === 'communal'), [units]);

  return { units, loading, search, setSearch, areaType, setAreaType, statusFilter, setStatusFilter, flats, communal };
}

function UnitListSection({ title, subtitle, units, navigate, accent }) {
  if (!units.length) return null;
  const color = accent === 'communal' ? 'text-emerald-700 border-emerald-200 bg-emerald-50' : 'text-blue-700 border-blue-200 bg-blue-50';
  return (
    <div className="space-y-3" data-testid={`section-${accent}`}>
      <div className={`flex items-center gap-3 px-3 py-2 rounded-lg border ${color}`}>
        {accent === 'communal' ? <Layers className="h-4 w-4" /> : <Building className="h-4 w-4" />}
        <div className="flex-1">
          <h2 className="text-sm font-bold tracking-tight" style={{ fontFamily: 'Outfit' }}>{title}</h2>
          {subtitle && <p className="text-[11px] opacity-80">{subtitle}</p>}
        </div>
        <span className="text-xs font-semibold">{units.length}</span>
      </div>
      <div className="grid grid-cols-1 gap-2">
        {units.map(unit => (
          <UnitCard key={unit.id} unit={unit} onClick={() => navigate(`/units/${unit.id}`)} />
        ))}
      </div>
    </div>
  );
}

export default function Dashboard() {
  const navigate = useNavigate();
  const { units, loading, search, setSearch, areaType, setAreaType, statusFilter, setStatusFilter, flats, communal } = useUnitsData();
  const showFlats = (areaType === 'all' || areaType === 'flat') && flats.length > 0;
  const showCommunal = (areaType === 'all' || areaType === 'communal') && communal.length > 0;

  const downloadFile = async (url, filename) => {
    try {
      const resp = await api.get(url, { responseType: 'blob' });
      const blobUrl = URL.createObjectURL(resp.data);
      const a = document.createElement('a'); a.href = blobUrl; a.download = filename; a.click();
      URL.revokeObjectURL(blobUrl);
    } catch { toast.error('Download failed'); }
  };

  return (
    <Layout>
      <div className="space-y-4" data-testid="dashboard-page">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Outfit' }}>Dashboard</h1>
          <p className="text-sm text-slate-500 mt-1">60 East Street — Purple Pepper Partnership Ltd</p>
        </div>
        {/* Export buttons */}
        <div className="flex flex-wrap gap-2">
          <Button size="sm" variant="outline" className="text-xs" onClick={() => downloadFile('/reports/summary/pdf', 'project_summary.pdf')} data-testid="export-summary-pdf">
            <FileText className="h-3 w-3 mr-1" />Summary PDF
          </Button>
          <Button size="sm" variant="outline" className="text-xs" onClick={() => downloadFile('/exports/csv', 'checklist_export.csv')} data-testid="export-csv">
            <Download className="h-3 w-3 mr-1" />CSV
          </Button>
          <Button size="sm" variant="outline" className="text-xs" onClick={() => downloadFile('/exports/excel', 'project_export.xlsx')} data-testid="export-excel">
            <Download className="h-3 w-3 mr-1" />Excel
          </Button>
        </div>
        {/* Legend */}
        <div className="flex items-center gap-4 flex-wrap">
          <div className="flex items-center gap-1.5"><div className="w-2.5 h-2.5 rounded-full bg-blue-500" /><span className="text-xs text-slate-500">Not Started</span></div>
          <div className="flex items-center gap-1.5"><div className="w-2.5 h-2.5 rounded-full bg-amber-500" /><span className="text-xs text-slate-500">In Progress</span></div>
          <div className="flex items-center gap-1.5"><div className="w-2.5 h-2.5 rounded-full bg-emerald-500" /><span className="text-xs text-slate-500">Complete</span></div>
          <div className="flex items-center gap-1.5"><div className="w-2.5 h-2.5 rounded-full bg-red-500" /><span className="text-xs text-slate-500">Blocked</span></div>
        </div>
        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search by flat number or floor..."
              className="pl-9" data-testid="search-input"
            />
          </div>
          <Select value={areaType} onValueChange={setAreaType}>
            <SelectTrigger className="w-full sm:w-36" data-testid="area-type-filter"><SelectValue placeholder="Area type" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Areas</SelectItem>
              <SelectItem value="flat">Flats</SelectItem>
              <SelectItem value="communal">Communal</SelectItem>
            </SelectContent>
          </Select>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-full sm:w-36" data-testid="status-filter"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="not_started">Not Started</SelectItem>
              <SelectItem value="in_progress">In Progress</SelectItem>
              <SelectItem value="complete">Complete</SelectItem>
              <SelectItem value="blocked">Blocked</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {loading ? (
          <div className="flex items-center justify-center py-16 text-slate-400" data-testid="loading-indicator">Loading units...</div>
        ) : (
          <div className="space-y-6">
            {showCommunal && (
              <UnitListSection title="Communal Areas" subtitle="Shared spaces (displayed first)" units={communal} navigate={navigate} accent="communal" />
            )}
            {showFlats && (
              <UnitListSection title="Flats" subtitle="Individual residential units" units={flats} navigate={navigate} accent="flat" />
            )}
            {units.length === 0 && (
              <div className="text-center py-12 text-slate-400">No units match your filters</div>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
}
